import { AfterViewInit, Directive, ElementRef, HostListener, Input } from '@angular/core';

@Directive({
    selector: '[zipCodesTextarea]',
    standalone: false
})
export class ZipCodesTextareaDirective implements AfterViewInit {
    @Input() maxRows = 4;
    @Input() enableAutoResize = true;

    private readonly zws = '\u200B';

    constructor(private el: ElementRef<HTMLTextAreaElement>) {}

    ngAfterViewInit(): void {
        this.scheduleAdjust();
    }

    @HostListener('input', ['$event'])
    onInput(event: Event): void {
        const textarea = this.el.nativeElement;
        const inputEvent = event as InputEvent;
        let cursorPos = textarea.selectionStart || 0;
        let rawValue = textarea.value;

        if (inputEvent.inputType === 'deleteContentBackward' && cursorPos > 0) {
            const prevChar = rawValue[cursorPos - 1];
            const prevPrevChar = rawValue[cursorPos - 2];
            if (prevChar === this.zws && prevPrevChar === ',') {
                rawValue = rawValue.slice(0, cursorPos - 2) + rawValue.slice(cursorPos);
                cursorPos -= 2;
            } else if (prevChar === ',') {
                rawValue = rawValue.slice(0, cursorPos - 1) + rawValue.slice(cursorPos);
                cursorPos -= 1;
            }
        }

        const normalized = this.normalizeValue(rawValue, cursorPos);
        if (normalized.value !== textarea.value) {
            textarea.value = normalized.value;
            textarea.setSelectionRange(normalized.cursorPos, normalized.cursorPos);
        }

        this.adjust(textarea);
    }

    @HostListener('blur')
    onBlur(): void {
        this.stripZeroWidth();
    }

    @HostListener('change')
    onChange(): void {
        this.stripZeroWidth();
    }

    private stripZeroWidth(): void {
        const textarea = this.el.nativeElement;
        const cleaned = textarea.value.replace(/\u200B/g, '');
        if (cleaned !== textarea.value) {
            textarea.value = cleaned;
        }
    }

    private normalizeValue(value: string, cursorPos: number): { value: string; cursorPos: number } {
        const beforeCursor = value.slice(0, cursorPos);
        const zwsBefore = (beforeCursor.match(/\u200B/g) || []).length;
        const cleaned = value.replace(/\u200B/g, '');
        const cleanedCursor = Math.max(0, cursorPos - zwsBefore);
        let output = '';
        let newCursor = cleanedCursor;

        for (let i = 0; i < cleaned.length; i += 1) {
            if (cleaned[i] === ',') {
                output += `,${this.zws}`;
                if (cleanedCursor > i) {
                    newCursor += 1;
                }
                continue;
            }
            output += cleaned[i];
        }

        return { value: output, cursorPos: newCursor };
    }

    private adjust(textarea: HTMLTextAreaElement): void {
        if (!this.enableAutoResize) {
            return;
        }
        const styles = getComputedStyle(textarea);
        const lineHeight = Number.parseFloat(styles.lineHeight) || 20;
        const paddingTop = Number.parseFloat(styles.paddingTop) || 0;
        const paddingBottom = Number.parseFloat(styles.paddingBottom) || 0;
        const maxHeight = (lineHeight * this.maxRows) + paddingTop + paddingBottom;
        textarea.style.height = 'auto';
        const nextHeight = Math.min(textarea.scrollHeight, maxHeight);
        textarea.style.height = `${nextHeight}px`;
        textarea.style.overflowY = textarea.scrollHeight > maxHeight ? 'auto' : 'hidden';
    }

    private scheduleAdjust(): void {
        setTimeout(() => this.adjust(this.el.nativeElement), 0);
    }
}
